import React from 'react';
import Modal from '.';

import api from '../../services/api';
import ButtonV2 from '../button/button_v2';
import undrawAlert from '../../assets/icons/undraw-alert.svg';

import * as S from './stylesTotvs';
import { BodyM, H5 } from '../../styles/stylesTypography';

const LEGISLACAO_URL = 'https://www.legisweb.com.br/legislacao/?id=475798';
const CAR_URL = 'https://www.car.gov.br/#/consultar';

class ModalFarmCoords extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loadingEdit: false,
            loadingGeocode: false,
            errorEdit: null,
            errorGeocode: null,
            errorGeocodeNoRetry: false,
        };

        this.handleEditCoords = this.handleEditCoords.bind(this);
        this.handleGeocode = this.handleGeocode.bind(this);
    }

    safeErrorMessage(error, fallbackMessage) {
        if (!error) return fallbackMessage;
        if (typeof error === 'string') return error;
        if (Array.isArray(error?.response?.data?.errors) && error.response.data.errors.length > 0) {
            return error.response.data.errors.join('; ');
        }
        if (typeof error?.response?.data?.errors === 'string' && error.response.data.errors.trim() !== '') {
            return error.response.data.errors;
        }
        if (error?.message && typeof error.message === 'string') return error.message;
        return fallbackMessage;
    }

    buildFarmAddress(farm) {
        const parts = [
            farm?.endereco,
            farm?.bairro,
            farm?.numero,
            farm?.cidade ? `${farm.cidade}-${farm?.estado || ''}` : farm?.estado,
        ]
            .filter(Boolean)
            .map((p) => String(p).trim())
            .filter((p) => p !== '');

        return parts.join(', ');
    }

    loadGoogleMapsAPI() {
        return new Promise((resolve, reject) => {
            if (window.google && window.google.maps && window.google.maps.Geocoder) {
                resolve();
                return;
            }

            const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
            if (existingScript) {
                existingScript.remove();
            }

            window.initGoogleMaps = () => {
                if (window.google && window.google.maps && window.google.maps.Geocoder) {
                    delete window.initGoogleMaps;
                    resolve();
                } else {
                    delete window.initGoogleMaps;
                    reject(new Error('Google Maps API não inicializou corretamente'));
                }
            };

            const script = document.createElement('script');
            script.src =
                'https://maps.googleapis.com/maps/api/js?key=AIzaSyAn3UaVotrho9E6f_BWtW4wDshxnEmM8LI&libraries=places,drawing&loading=async&callback=initGoogleMaps';
            script.async = true;
            script.defer = true;

            script.onerror = () => {
                reject(new Error('Erro ao carregar Google Maps API'));
            };

            setTimeout(() => {
                if (window.initGoogleMaps) {
                    delete window.initGoogleMaps;
                }
                reject(new Error('Timeout ao carregar Google Maps API'));
            }, 15000);

            document.head.appendChild(script);
        });
    }

    async geocodeWithGoogleMaps(address) {
        await this.loadGoogleMapsAPI();

        return new Promise((resolve, reject) => {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address }, (results, status) => {
                if (status === 'OK' && results[0]) {
                    const location = results[0].geometry.location;
                    resolve({ lat: location.lat(), lng: location.lng() });
                } else {
                    reject(new Error(`Geocoding falhou: ${status}`));
                }
            });
        });
    }

    async geocodeWithAlternativeAPI(address) {
        const response = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
        );
        const data = await response.json();

        if (data && data.length > 0) {
            const lat = parseFloat(data[0].lat);
            const lng = parseFloat(data[0].lon);
            return { lat, lng };
        }

        return null;
    }

    async generateCoordinatesFromAddress(address) {
        try {
            const result = await this.geocodeWithGoogleMaps(address);
            if (result) return result;
        } catch (_) {
            const result = await this.geocodeWithAlternativeAPI(address);
            if (result) return result;
        }

        return null;
    }

    async updateFarmCoords(farmId, coords) {
        const response = await api.get(`/fazendas/${farmId}`);
        const farm = response?.data?.data || {};

        const payload = {
            nome: farm.nome,
            cpf_cnpj: farm.cpf_cnpj,
            area_total: farm.area_total != null ? String(farm.area_total) : '',
            cod_ibge: farm.cod_ibge,
            cep: farm.cep,
            endereco: farm.endereco,
            numero: farm.numero != null ? String(farm.numero) : '',
            complemento: farm.complemento != null ? String(farm.complemento) : '',
            bairro: farm.bairro,
            cidade: farm.cidade,
            estado: farm.estado,
            altitude: farm.altitude != null ? String(farm.altitude) : '',
            status: farm.status != null ? Number(farm.status) : 1,
            inscricao_estadual: farm.inscricao_estadual,
            inscricao_orgao: farm.inscricao_orgao,
            cadastro_ambiental: farm.cadastro_ambiental,
            endereco_manual: farm.endereco_manual,
            latitude: String(coords.lat),
            longitude: String(coords.lng),
        };

        try {
            return await api.put(`/fazendas/${farmId}`, payload);
        } catch (error) {
            const maybeNotFound = error?.response?.status === 404;
            if (!maybeNotFound) throw error;
            return await api.put(`/fazendas/${farmId}/`, payload);
        }
    }

    async handleEditCoords() {
        if (this.state.loadingEdit || this.state.loadingGeocode) return;

        this.setState({ loadingEdit: true, errorEdit: null, errorGeocode: null });

        try {
            if (!this.props.onEditFarmCoords) {
                throw new Error('Não foi possível abrir o cadastro de coordenadas. Tente novamente.');
            }

            const result = await Promise.resolve(this.props.onEditFarmCoords());

            if (this.props.onCompleted) {
                this.props.onCompleted({ action: 'edit', result });
            }

            if (this.props.onHide) {
                this.props.onHide();
            }
        } catch (error) {
            this.setState({
                errorEdit: this.safeErrorMessage(
                    error,
                    'Não foi possível abrir o cadastro de coordenadas. Tente novamente.'
                ),
            });
        } finally {
            this.setState({ loadingEdit: false });
        }
    }

    async handleGeocode() {
        if (this.state.loadingEdit || this.state.loadingGeocode) return;

        this.setState({ loadingGeocode: true, errorEdit: null, errorGeocode: null });

        try {
            const farmId = this.props.farmId;
            if (!farmId) {
                throw new Error('Não foi possível identificar a Fazenda para gerar as coordenadas. Tente novamente.');
            }

            let result;
            if (this.props.onGeocodeFarm) {
                result = await Promise.resolve(this.props.onGeocodeFarm());
            } else {
                const response = await api.get(`/fazendas/${farmId}`);
                const farm = response?.data?.data || {};
                const address = this.buildFarmAddress(farm);

                if (!address) {
                    throw new Error('Endereço da Fazenda incompleto. Complete o cadastro para gerar automaticamente.');
                }

                const coords = await this.generateCoordinatesFromAddress(address);
                if (!coords) {
                    throw new Error('Não foi possível localizar o endereço da Fazenda para gerar as coordenadas.');
                }

                await this.updateFarmCoords(farmId, coords);
                result = coords;
            }

            if (this.props.onCompleted) {
                this.props.onCompleted({ action: 'geocode', result });
            }

            if (this.props.onHide) {
                this.props.onHide();
            }
        } catch (error) {
            const is400 = error?.response?.status === 400;
            this.setState({
                errorGeocode: is400
                    ? 'Não foi possivel gerar as coordenadas automaticamente, pois o CEP informado na fazenda é inválido. Faça a atualização manualmente.'
                    : this.safeErrorMessage(
                          error,
                          'Não foi possível gerar as coordenadas automaticamente. Tente novamente.'
                      ),
                errorGeocodeNoRetry: is400,
            });
        } finally {
            this.setState({ loadingGeocode: false });
        }
    }

    renderError(message, onRetry) {
        if (!message) return null;

        return (
            <BodyM color="#FF5252" style={{ marginTop: '12px' }}>
                {message}{' '}
                {onRetry && (
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={onRetry}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' ? onRetry() : null)}
                        style={{ color: '#589bff', cursor: 'pointer', fontWeight: 700 }}
                    >
                        Tentar novamente
                    </span>
                )}
            </BodyM>
        );
    }

    render() {
        const { loadingEdit, loadingGeocode, errorEdit, errorGeocode, errorGeocodeNoRetry } = this.state;
        const { blocking = true, marginBottom, show } = this.props;

        return (
            <Modal
                show={show}
                onHide={blocking ? undefined : this.props.onHide}
                align="center"
                justify="center"
                responsive
            >
                <S.ModalBody marginBottom={marginBottom}>
                    <S.ContainerModalTotvs>
                        <img src={undrawAlert} alt="Alerta" />

                        <H5>Coordenadas geográficas não cadastradas</H5>

                        <BodyM color="#303848">
                            Segundo a{' '}
                            <a
                                href={LEGISLACAO_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#589bff' }}
                            >
                                Resolução CONFEA Nº 1149
                            </a>
                            , é obrigatório informar as coordenadas geográficas da propriedade rural para emissão de
                            receitas em todo o Brasil. Para continuar, cadastre as coordenadas manualmente com base no{' '}
                            <a
                                href={CAR_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#589bff' }}
                            >
                                CAR
                            </a>{' '}
                            ou gere automaticamente com base no endereço da Fazenda.
                        </BodyM>

                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
                            <ButtonV2
                                width="200px"
                                height="48px"
                                value="CADASTRAR COORDENADAS (recomendado)"
                                outline={false}
                                loading={loadingEdit}
                                disabled={loadingGeocode}
                                onClick={this.handleEditCoords}
                            />

                            <ButtonV2
                                width="200px"
                                height="48px"
                                value="GERAR AUTOMATICAMENTE"
                                outline={false}
                                loading={loadingGeocode}
                                disabled={loadingEdit}
                                onClick={this.handleGeocode}
                            />
                        </div>

                        {this.renderError(errorEdit, () => this.setState({ errorEdit: null }))}
                        {this.renderError(errorGeocode, errorGeocodeNoRetry ? null : () => this.setState({ errorGeocode: null, errorGeocodeNoRetry: false }))}
                    </S.ContainerModalTotvs>
                </S.ModalBody>
            </Modal>
        );
    }
}

export default ModalFarmCoords;
